import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Mail,
  Phone,
} from "lucide-react";
import TeroumaLogo from "../components/ui/TeroumaLogo";
import { PUBLIC_API } from "../lib/api";
import { useI18n } from "../i18n";
import LanguageSwitcher from "../i18n/LanguageSwitcher";

/**
 * /verify-identity/:token — the page the donor lands on after clicking the
 * magic link in their email or SMS. One click → identity confirmed → the
 * original page (still open on another tab/device) unlocks automatically.
 *
 * No form to re-fill, no payment, nothing else. The unique job of this page
 * is to flip ``verified=true`` on the verification token.
 */
export default function VerifyIdentityPage() {
  const { token } = useParams();
  const { t } = useI18n();
  const [status, setStatus] = useState("loading"); // loading|ready|confirming|confirmed|error|expired
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");

  // On mount, fetch token status (so we can show the org / contact correctly)
  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError(t("verify.err_invalid"));
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${PUBLIC_API}/public/identity/${token}/status`);
        if (r.data?.expired) {
          setStatus("expired");
          return;
        }
        if (r.data?.verified) {
          // Already verified — confirm in idempotent way + show success
          setStatus("confirmed");
          setInfo(r.data);
          return;
        }
        setInfo(r.data);
        setStatus("ready");
      } catch (e) {
        setStatus("error");
        setError(e?.response?.data?.detail || t("verify.err_not_found"));
      }
    })();
  }, [token]);

  const confirm = async () => {
    setStatus("confirming");
    try {
      const r = await axios.post(
        `${PUBLIC_API}/public/identity/${token}/confirm`,
      );
      setInfo(r.data);
      setStatus("confirmed");
    } catch (e) {
      setStatus("error");
      setError(e?.response?.data?.detail || t("verify.err_confirm"));
    }
  };

  const renderBody = () => {
    if (status === "loading") {
      return (
        <div className="flex flex-col items-center gap-3 py-12">
          <Loader2 className="h-7 w-7 animate-spin text-stone-400" />
          <p className="text-sm text-stone-500">{t("common.loading")}</p>
        </div>
      );
    }

    if (status === "expired") {
      return (
        <div className="flex flex-col items-center text-center gap-3">
          <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold text-stone-900">{t("verify.expired_title")}</h2>
          <p className="text-sm text-stone-500 max-w-sm">
            {t("verify.expired_body")}
          </p>
          <Link
            to="/"
            className="mt-2 text-sm text-stone-700 underline hover:text-stone-900"
          >
            {t("verify.back_home")}
          </Link>
        </div>
      );
    }

    if (status === "error") {
      return (
        <div className="flex flex-col items-center text-center gap-3">
          <div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-red-600" />
          </div>
          <h2 className="text-lg font-semibold text-stone-900">{t("verify.invalid_title")}</h2>
          <p className="text-sm text-stone-500 max-w-sm">{error}</p>
          <Link
            to="/"
            className="mt-2 text-sm text-stone-700 underline hover:text-stone-900"
          >
            {t("verify.back_home")}
          </Link>
        </div>
      );
    }

    if (status === "confirmed") {
      return (
        <div className="flex flex-col items-center text-center gap-3">
          <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="h-9 w-9 text-emerald-600" />
          </div>
          <h2 className="text-xl font-semibold text-stone-900">
            {t("verify.confirmed_title")}
          </h2>
          <p className="text-sm text-stone-600 max-w-md leading-relaxed">
            {info?.first_name
              ? t("verify.thanks_named", { name: info.first_name })
              : t("verify.thanks")}{" "}
            {t("verify.confirmed_before")}{" "}
            <strong>{t("verify.confirmed_strong")}</strong>{" "}
            {t("verify.confirmed_after")}
          </p>
          {info?.org_name && (
            <p className="text-xs text-stone-400 mt-1">
              {t("verify.org_label", { org: info.org_name })}
            </p>
          )}
        </div>
      );
    }

    // status === "ready" or "confirming"
    return (
      <div className="flex flex-col items-center text-center gap-4">
        <div className="h-14 w-14 rounded-full bg-stone-900 flex items-center justify-center">
          <ShieldCheck className="h-7 w-7 text-white" />
        </div>
        <h2 className="text-xl font-semibold text-stone-900">
          {t("verify.confirm_title")}
        </h2>
        <div className="flex items-center gap-2 text-stone-600 text-sm">
          {info?.channel === "sms" ? (
            <Phone className="h-4 w-4" />
          ) : (
            <Mail className="h-4 w-4" />
          )}
          {info?.masked_destination}
        </div>
        <p className="text-sm text-stone-500 max-w-md leading-relaxed">
          {t("verify.intro")}
        </p>
        <button
          onClick={confirm}
          disabled={status === "confirming"}
          className="mt-2 w-full sm:w-auto px-10 py-3 rounded-2xl bg-stone-900 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {status === "confirming" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
          {t("verify.cta")}
        </button>
        <p className="text-[11px] text-stone-400 max-w-md mt-2">
          {t("verify.footnote")}
        </p>
      </div>
    );
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: "#F4F3EF", fontFamily: "'Outfit', sans-serif" }}
    >
      <nav className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/">
            <TeroumaLogo textColor="#0D1B33" />
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-stone-400">{t("verify.secure_badge")}</span>
            <LanguageSwitcher />
          </div>
        </div>
      </nav>

      <div className="max-w-md mx-auto px-6 py-12">
        <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-8">
          {renderBody()}
        </div>
      </div>
    </div>
  );
}
